/// <reference path="../../app.definition.ts" />

import angular from "angular";
import _ from "lodash";
import { ViewState } from "@common/model/searchState";
import { Subject } from "@common/model/subject";
import { User } from "@common/model/user";
import { UserExperience } from "@common/model/userExperience";
import * as jsonLogic from 'json-logic-js';

export class CacheService {
    static $inject = ["$q"];
    storage;
    //#41359 - IE did not persist session storage even after clean up
    //store invite code in seperate variable other than session storage
    inviteCode: string;
    token: string;
    vkResetCode: string;
    public user: User;
    allsubjects: Subject[];
    currentUser;
    memberName;
    subjectName;
    savedStates;
    adminInvite;
    inviteStatus;
    isInvitationExpired;
    timeUnits;
    isVaultKeyExpired;
    formsCachedData;
    $q;
    isVaultKeyResetRequiredMsg;
    public subjects;
    public currentSubject;

    constructor($q) {
        this.storage = window.sessionStorage;
        this.$q = $q;
    }

    get(key, requireDecoding = false) {
        try {
            if (!_.isNil(this.storage[key])) {
                let value = this.storage[key];
                // decode value to base64 when required
                requireDecoding && (value = atob(value));
                try {
                    return JSON.parse(value);
                } catch (e) {
                    return value;
                }
            } else {
                return undefined;
            }
        } catch (e) {
            console.debug("cannot set the session storage");
        }
    }

    set(key, value, requireEncoding = false) {
        try {
            if (_.isNil(value)) {
                this.remove(key); // remove null or undefined values from storage
            } else {
                // store primitives as primitives - stringify all else
                value = [
                    "string",
                    "number",
                    "boolean",
                    "bigint",
                ].includes(typeof value)
                    ? value
                    : JSON.stringify(value);
                // encode value to base64 when required
                requireEncoding && (value = btoa(value));
                this.storage[key] = value;
            }
        } catch (e) {
            console.debug("cannot set the session storage")
        }


    }

    remove(key) {
        if (!_.isNil(this.storage[key])) {
            delete this.storage[key];
        }
    }

    removeAll() {
        this.storage.clear();
    }

    setTabCloseTime(closeTime) {
        this.set("closeTime", closeTime);
    }

    getTabCloseTime() {
        return this.get("closeTime");
    }

    setAuthResponse(res) {
        this.set("response", res);
    }

    getAuthResponse() {
        return this.get("response");
    }

    setNotFirstTime(firstTime) {
        this.set("firstTime", firstTime);
    }

    getNotFirstTime() {
        return this.get("firstTime");
    }

    setLogOut() {
        this.set("isLogOut", false);
    }

    getLogOut() {
        return this.get("isLogOut")
    }

    setToken(token: string) {
        this.token = token;
    }

    getToken() {
        return this.token;
    }

    setUser(user: User) {
        user = new User().deserialize(user);
        this.set("user", user);
        this.user = user;
        this.currentUser = user;
    }

    getUser() {
        var u: User;
        if (this.get("user") && this.get("user") instanceof User) {
            u = new User().deserialize(this.get("user"));
            this.currentUser = u;

            return u;
        } else {
            u = this.get("user");
            this.currentUser = u;

            return u;
        }
    }



    setUserExperience(userExperience: UserExperience) {
        this.set("userExperience", userExperience);
    }

    getUserExperience() {
        return this.get("userExperience");
    }

    setHideBigLogo(value: boolean) {
        this.set("HideBigLogo", value);
    }

    getHideBigLogo() {
        return this.get("HideBigLogo");
    }

    setTokenValidation(validation) {
        this.set("tokenValidation", validation);
    }

    getTokenValidation() {
        return this.get("tokenValidation");
    }

    setCurrentSubject(subject: Subject) {
        this.set("currentSubject", subject);
        this.currentSubject = subject;
    }

    getCurrentSubject() {
        var s = this.get("currentSubject");
        if (s) {
            if (s.birthDate) {
                let dd = new Date(s.birthDate);
                s.birthDateShort = new Date(
                    dd.getUTCFullYear(),
                    dd.getUTCMonth(),
                    dd.getUTCDate()
                );
            }
            if (s.startDate) {
                let dd = new Date(s.startDate);
                s.startDateShort = new Date(
                    dd.getUTCFullYear(),
                    dd.getUTCMonth(),
                    dd.getUTCDate()
                );
            }
            if (s.endDate) {
                let dd = new Date(s.endDate);
                s.endDateShort = new Date(
                    dd.getUTCFullYear(),
                    dd.getUTCMonth(),
                    dd.getUTCDate()
                );
            }
        }
        return s;
    }

    setCrntTeamCardId(subjectId: string) {
        this.set("crntTeamCardId", subjectId);
    }

    getCrntTeamCardId() {
        return this.get("crntTeamCardId");
    }

    setPassword(password: string) {
        if (!!password) this.set("dummy", password, true);
        else this.set("dummy", null);
    }

    getPassword() {
        if (!!this.get("dummy", true)) return this.get("dummy", true);
        return null;
    }

    setVKResetCode(i) {
        this.vkResetCode = i;
        this.set("vkResetCode", i);
    }

    getVKResetCode = () =>
        this.vkResetCode || this.get("vkResetCode") || null;

    setAuthCode(authCode) {
        this.set("authCode", authCode);
    }

    getAuthCode() {
        return this.get("authCode");
    }

    setIdToken(idToken) {
        this.set("idToken", idToken);
    }

    getIdToken() {
        return this.get("idToken");
    }

    setAuthToken(authToken) {
        this.set("authToken", authToken);
    }

    getAuthToken() {
        return this.get("authToken");
    }

    //Sorted subjects list cache
    setSortedSubjects(allsubjects: Subject[]) {
        this.set("sortedSubjects", allsubjects);
    }

    getSortedSubjects(): Subject[] {
        return this.get("sortedSubjects") ? this.get("sortedSubjects") : [];
    }

    //Randomly fetched subjects cache
    setSubjects(allsubjects: Subject[]) {
        this.set("subjects", allsubjects);
    }

    getSubjects(): Subject[] {
        return this.get("subjects") ? this.get("subjects") : [];
    }

    setTotalSubjects(cnt: number) {
        this.set("totalSubjects", cnt);
    }

    getTotalSubjects() {
        return this.get("totalSubjects");
    }

    hasSingleSubject(): Boolean {
        return this.get("totalSubjects").length == 1;
    }

    setAuthType(authType) {
        this.set("authType", authType);
    }

    getAuthType() {
        return this.get("authType");
    }

    getUserSignature() {
        if (this.get("userSignature")) {
            return this.get("userSignature");
        } else {
            return null;
        }
    }

    setUserSignature(obj) {
        this.set("userSignature", obj);
    }
    setShowCoachMark(t) {
        this.set("showCoach", t);
    }


    getShowCoachMark() {
        return this.get("showCoach");
    }

    reset() {
        this.removeAll();
        this.inviteCode = null;
        this.token = null;
        this.vkResetCode = null;
        this.user = null;
        this.allsubjects = null;
        this.subjects = null;
        this.currentSubject = null;
        this.storage.isLogOut = true;
        this.storage.planType = null;
        this.storage.feature = null;
        this.storage.cachedImages = null;
        this.resetInviteInfo();
    }

    setMemberName(memberName: string): void {
        !!memberName && (this.memberName = memberName);
    }

    setSubjectName(subjectName: string): void {
        !!subjectName && (this.subjectName = subjectName);
    }

    setAdminInvite(adminInvite: boolean): void {
        !!adminInvite && (this.adminInvite = adminInvite);
    }

    setInviteStatus(inviteStatus: string): void {
        !!inviteStatus && (this.inviteStatus = inviteStatus);
    }

    setInvitationExpired(isInvitationExpired: boolean): void {
        !!isInvitationExpired &&
            (this.isInvitationExpired = isInvitationExpired);
    }

    getInviteInfo(): any {
        return {
            memberName: this.memberName || null,
            subjectName: this.subjectName || null,
            adminInvite: this.adminInvite || false,
            status: this.inviteStatus || null,
            isInvitationExpired: this.isInvitationExpired,
        };
    }

    setTimeUnits(units: Object): void {
        this.timeUnits = units;
    }

    getTimeUnits(): any {
        return this.timeUnits;
    }

    setVaultKeyResetRequired(isVaultKeyResetRequired: boolean): void {
        this.set("isVaultKeyResetRequired", isVaultKeyResetRequired);
    }

    isVaultKeyResetRequired(): boolean {
        return this.get("isVaultKeyResetRequired");
    }

    setVaultKeyResetRequiredMessage(
        isVaultKeyResetRequiredMessage: boolean
    ): void {
        this.set(
            "isVaultKeyResetRequiredMessage",
            isVaultKeyResetRequiredMessage
        );
    }

    getVaultKeyResetRequiredMessage(): boolean {
        return this.get("isVaultKeyResetRequiredMessage");
    }

    getFormsCachedData() {
        return this.get("formsCachedData");
    }

    setFormsCachedData(formsCachedData: any) {
        this.set("formsCachedData", formsCachedData);
    }

    resetInviteInfo(): void {
        this.memberName = null;
        this.subjectName = null;
        this.adminInvite = false;
        this.inviteStatus = null;
        this.isInvitationExpired = null;
    }

    setPostBoardState(postBoardState: any): void {
        this.set("postBoardState", postBoardState);
    }

    getPostBoardState(): any {
        return this.get("postBoardState");
    }

    setPreviousSubject(subject: any): void {
        this.set("previousSubject", subject);
    }

    getPreviousSubject(): any {
        return this.get("previousSubject");
    }

    // set time stamp for last loaded feed on page
    setLastLoadedFeedTime(timeStamp: any): any {
        this.set("lastLoadedFeedTime", timeStamp);
    }

    // get last loaded feed time
    getLastLoadedFeedTime(): any {
        return this.get("lastLoadedFeedTime")
            ? this.get("lastLoadedFeedTime")
            : null;
    }

    setUpdateIndication(type: string, value: boolean): void {
        var updateIndication = this.get("updateIndication");
        if (!!updateIndication) {
            updateIndication[type] = value;
        } else {
            updateIndication = {
                discussions: false,
                posts: false,
                documents: false,
            };
            updateIndication[type] = value;
        }
        this.set("updateIndication", updateIndication);
    }

    getUpdateIndication(type: string): boolean {
        let updateIndication = this.get("updateIndication");

        if (updateIndication && updateIndication.hasOwnProperty(type)) {
            return updateIndication[type];
        }

        return false;
    }

    setSavedStates(states: ViewState[]) {
        this.set("savedStates", states);
    }

    getSavedStates() {
        return this.get("savedStates") || [];
    }

    setTempTeamMember(user: any) {
        this.set("tempTeammember", user);
    }

    getTempTeamMember() {
        return this.get("tempTeammember")
            ? this.get("tempTeammember")
            : null;
    }

    setTempDocument(document: any) {
        this.set("tempDocument", document);
    }

    getTempDocument() {
        return this.get("tempDocument") ? this.get("tempDocument") : null;
    }

    setTempDiscussion(discussion: any) {
        this.set("tempDiscussion", discussion);
    }

    getTempDiscussion() {
        return this.get("tempDiscussion")
            ? this.get("tempDiscussion")
            : null;
    }

    setPushNotifications(notifications: any) {
        this.set("pushNotifications", notifications);
    }

    getPushNotifications() {
        return this.get("pushNotifications")
            ? this.get("pushNotifications")
            : [];
    }

    setTotalNotifications(cnt: number) {
        this.set("totalNotifications", cnt);
    }

    getTotalNotifications() {
        return this.get("totalNotifications");
    }

    setNotViewedCount(viewedCnt: any) {
        this.set("notViewedCount", viewedCnt);
    }

    getNotViewedCount() {
        let def = {
            DOCUMENT: 0,
            DISCUSSION: 0,
        };
        return this.get("notViewedCount")
            ? this.get("notViewedCount")
            : def;
    }

    setSelectedPlan(plan: any) {
        this.set("selectedPlan", plan);
    }

    getSelectedPlan() {
        return this.get("selectedPlan") ? this.get("selectedPlan") : null;
    }

    setPInfo(planInfo) {
        this.set("pInfo", planInfo, true);
    }

    getPInfo() {
        return this.get("pInfo", true) ? this.get("pInfo", true) : null;
    }

    setFeatures(features: any) {
        this.set("feature", features);
    }

    getFeatures() {
        return this.get("feature") ? this.get("feature") : null;
    }

    setUserPlanType(planType: string) {
        this.set("userp", (planType));
    }

    getUserPlanType() {
        return this.get("userp") ? (this.get("userp")) : null;
    }

    setCurrentSubjectInMemory(subject: any) {
        this.currentSubject = subject;
    }

    getCrSwitched() {
        return this.get("crSwitched") ? this.get("crSwitched") : false;
    }

    setCrSwitched(flag: boolean) {
        this.set("crSwitched", flag);
    }

    getFormListPagination() {
        return this.get("formListPagination");
    }

    setFormListPagination(pagination: any) {
        this.set("formListPagination", pagination);
    }

    getSrvcListPagination() {
        return this.get("srvcListPagination")
            ? this.get("srvcListPagination")
            : {};
    }

    setSrvcListPagination(pagination: any) {
        this.set("srvcListPagination", pagination);
    }

    getIsServicesAssigned() {
        return this.get("isServicesAssigned");
    }

    setIsServicesAssigned(flag: boolean) {
        this.set("isServicesAssigned", flag);
    }

    getIsPreviousService() {
        return this.get("isPreviousService");
    }

    setIsPreviousService(flag: boolean) {
        this.set("isPreviousService", flag);
    }

    getUserTeamPermissions() {
        return this.get("userTeamPermissions");
    }

    setUserTeamPermissions(permissions) {
        this.set("userTeamPermissions", permissions);
    }

    setListViewFlag(param: string, value: boolean): void {
        const listView = this.get("listView") || {};
        listView[param] = value;
        this.set("listView", listView);

    }

    isListView(param): boolean {
        const listView = this.get("listView");

        if (listView && listView.hasOwnProperty(param)) {
            return listView[param];
        }
        return undefined;
    }

    setImageCache(imageCache) {
        this.set("cachedImages", imageCache);
    }

    getImageCache() {
        var cachedImages = this.get("cachedImages");
        if (cachedImages) {
            return cachedImages;
        } else {
            this.set("cachedImages", []);
            return this.get("cachedImages");
        }
    }

    setMaxCncrntDwnld(max: number) {
        this.set("maxCncrntDwnld", max);
    }

    getMaxCncrntDwnld() {
        return this.get("maxCncrntDwnld");
    }

    setMaxCombineDocs(max: number) {
        this.set("maxCombineDocuments", max);
    }

    getMaxCombineDocs() {
        return this.get("maxCombineDocuments");
    }

    getFilteredCurrentSubject(filter) {
        var deferred = this.$q.defer();
        var subject = this.getCurrentSubject();
        if (jsonLogic.apply(filter, subject)) {
            deferred.resolve(subject);
        } else {
            deferred.resolve(undefined);
        }
        return deferred.promise;
    }

    setSASSToken(token) {
        this.set("sassToken", token);
    }

    getSASSToken() {
        return this.get("sassToken");
    }

    setRlFolderId(param) {
        this.set("rlFolderId", param);
    }

    getRlFolderId() {
        return this.get("rlFolderId");
    }

    setPostSearchResultsDetails(param) {
        this.set("postSearchResults", param);
    }

    setPostFilters(param) {
        this.set("postFilters", param);
    }

    getPostFilters() {
        return this.get("postFilters");
    }

    getPostSearchResultsDetails() {
        return this.get("postSearchResults");
    }

    getTeamInvitationTabsStatus(){
        return this.get("teamInvitationTabsStatus");
    }

    setTeamInvitationTabsStatus(param){
        this.set("teamInvitationTabsStatus", param);
    }

    /**
     * Store notification disabled flag
     * @param isDisabled true if ALL notifications are disabled, false if any are enabled
     */
    setNotificationDisabledFlag(isDisabled: boolean) {
        this.set("notificationDisabledFlag", isDisabled);
    }

    /**
     * Get notification disabled flag
     * Returns true if ALL notifications are disabled (push notifications OFF)
     * Returns false if at least some notifications are enabled (push notifications ON)
     */
    getNotificationDisabledFlag(): boolean {
        const flag = this.get("notificationDisabledFlag");
        // If no flag cached, assume notifications are enabled (not disabled)
        return flag === true;
    }
}

angular.module("eCareVault.common.services").service("CacheService", CacheService);
